

import { Component, Prop, Watch } from 'vue-property-decorator';
import ErrorMessageHandlerMixin from '../misc/ErrorMessageHandler.mixin';
import { mixins } from 'vue-class-component';
import { required, numeric } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';
import { namespace } from 'vuex-class';
import Driver from '@/models/Driver';
import { driverStoreActions } from '@/store/driver.store';
import Company from '@/models/Company';
import { applicationStoreGetter } from '@/store/application.store';
import { promoterStoreActions, promoterStoreGetters } from '@/store/promoter.store';
import Promoter from '@/models/Promoter';
import ReassignmentPreview from '@/models/ReassignmentPreview';
import moment from 'moment';
import { Country } from '@/misc/allCountries';
import { RepositoryFactory } from '@/api/RepositoryFactory';
import UploadRepository from '@/api/repositories/UploadRepository';
import FileRepository from '@/api/repositories/FileRepository';
import { compressImage } from '@/misc/imageCompression';

const DriverStore = namespace('driver');
const PromoterStore = namespace('promoter');
const ApplicationStore = namespace('application');

const uploadRepository: UploadRepository = RepositoryFactory.get('upload');
const fileRepository: FileRepository = RepositoryFactory.get('file');

/**
 * maximum edge length the driver photo is compressed to before uploading
 */
const AVATAR_MAX_EDGE = 1280;

@Component({
    mixins: [validationMixin],
    validations: {
        phoneNumber: { required, numeric },
        driverCopy: {
            firstName: {
                required
            },
            lastName: {
                required
            },
            driverLicense: {
                required
            }
        }
    },
    components: {
        CountyCodeComponent: () => import(
            /* webpackChunkName: "CountyCodeComponent" */
            '@/components/CountyCode.component.vue'),
        ReassignPromoterDialogComponent: () => import(
            /* webpackChunkName: "ReassignPromoterDialogComponent" */
            '@/components/Promoters/ReassignPromoterDialog.component.vue')
    }
})
export default class EditDriverComponent extends mixins(ErrorMessageHandlerMixin) {
    @Prop()
    public driver!: Driver | null;

    @ApplicationStore.Getter(applicationStoreGetter.SELECTED_COMPANY)
    public selectedCompany!: Company;

    @DriverStore.Action(driverStoreActions.CREATE)
    public createDriverAction!: (payload: { driver: Driver, company: Company }) => Promise<Driver>;

    @DriverStore.Action(driverStoreActions.UPDATE)
    public updateDriverAction!: (payload: { driver: Driver }) => Promise<Driver>;

    @PromoterStore.Action(promoterStoreActions.GET)
    private getPromotersAction!: (payload: {from: string, to: string}) => Promise<Promoter>;

    @PromoterStore.Getter(promoterStoreGetters.PROMOTERS)
    private promoters!: Promoter[];

    @PromoterStore.Action(promoterStoreActions.PREVIEW_REASSIGNMENT)
    private previewReassignmentAction!: (payload: { driver: string, toPromoter: string })
        => Promise<ReassignmentPreview>;

    @PromoterStore.Action(promoterStoreActions.APPLY_REASSIGNMENT)
    private applyReassignmentAction!: (payload: { driver: string, toPromoter: string, planHash: string })
        => Promise<void>;

    /**
     * The promoter the driver had when the dialog opened. Moving away from it
     * moves money between two promoters, so it cannot be a side effect of an
     * ordinary save -- the backend rejects a promoter change on driver.update
     * for the same reason.
     */
    private originalPromoterId: string | null = null;
    private showReassignDialog: boolean = false;
    private isApplyingReassignment: boolean = false;
    private reassignmentPreview: ReassignmentPreview | null = null;

    public driverCopy: Driver = new Driver();
    public validData = false;
    public isLoading: boolean = false;
    private prefix: string = '+264';
    private phoneNumber: string = '';

    /**
     * newly selected driver photo that is uploaded after saving
     */
    private avatarFile: File | null = null;

    /**
     * object url used to preview the selected or already stored photo
     */
    private avatarPreview: string = '';

    private onSelect(payload: Country): void {
        if (!payload) return;
        this.prefix = `+${payload.dialCode}`;
    }

    private async created() {
        if (this.driver) {
            this.driverCopy = Driver.parseFromObject({ ...this.driver });

            // the driver comes with the promoter populated, but the select works on ids
            if (this.driverCopy.promoter && typeof this.driverCopy.promoter === 'object') {
                this.driverCopy.promoter = (this.driverCopy.promoter as Promoter).id;
            }
            this.originalPromoterId = (this.driverCopy.promoter as string) ?? null;

            this.prefix = this.driverCopy.phone.prefix;
            this.phoneNumber = this.driverCopy.phone.phoneNumber;
            this.validData = true;
        }

        const today = moment(new Date()).format('yyyy-MM-DD');
        await this.getPromotersAction({ from: today, to: today });
        await this.loadExistingAvatar();
    }

    private beforeDestroy() {
        this.revokeAvatarPreview();
    }

    private async loadExistingAvatar() {
        if (!this.driver?.avatar) return;
        try {
            this.avatarPreview = await fileRepository.getFileObjectUrl(this.driver.avatar as string);
        } catch (e) {
            // existing photo couldn't be loaded, the placeholder is shown instead
        }
    }

    private revokeAvatarPreview() {
        if (this.avatarPreview) {
            URL.revokeObjectURL(this.avatarPreview);
            this.avatarPreview = '';
        }
    }

    private onPickAvatar() {
        (this.$refs.avatarInput as HTMLInputElement).click();
    }

    private onAvatarSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input.files && input.files.length > 0 ? input.files[0] : null;
        // reset the input so choosing the same file again triggers a change event
        input.value = '';
        if (!file || !file.type.startsWith('image/')) return;
        this.revokeAvatarPreview();
        this.avatarFile = file;
        this.avatarPreview = URL.createObjectURL(file);
    }

    /**
     * Compresses and uploads the selected photo for the given driver.
     * Called after the driver record itself was created/saved.
     */
    private async uploadAvatar(driverId: string) {
        if (!this.avatarFile || !driverId) return;
        try {
            const image = await compressImage(this.avatarFile, AVATAR_MAX_EDGE);
            await uploadRepository.uploadDriverAvatar({ id: driverId, image });
            this.avatarFile = null;
        } catch (e) {
            this.$notifyErrorSimplified('DRIVER.DIALOG.PHOTO_ERROR');
        }
    }

    private onDocumentsReadyChanged(value: boolean): void {
        if (value) return;
        this.driverCopy.active = false;
    }

    private get promoterOptions() {
        return [...this.promoters];
    }

    /**
     * User status to select from
     */
    private selectStatus: { text: string, value: boolean } [] = [
        { text: this.$t('COMPANY.DIALOG.STATUS_ACTIVE').toString(), value: true },
        { text: this.$t('COMPANY.DIALOG.STATUS_INACTIVE').toString(), value: false }
    ];

    @Watch('driver', { immediate: true })
    public driverChanged() {
        if (this.driver) {
            this.driverCopy = this.driver.copy() as Driver;
            this.validData = !this.$v!.$invalid;
        } else {
            this.driverCopy = Driver.parseFromObject({ active: false });
        }
    }

    public async saveData() {
        this.$v.$touch();
        if (this.$v.$invalid) return;

        const driverToUse = Driver.parseFromObject({
            ...this.driverCopy,
            phone: {
                prefix: this.prefix,
                phoneNumber: this.phoneNumber
            }
        });

        // A driver that already had a promoter and is being moved to a different
        // one goes through preview-then-confirm instead of saving directly.
        // Assigning one for the first time is an ordinary save -- there is
        // nothing to reverse.
        if (this.isPromoterBeingChanged) {
            await this.openReassignDialog();
            return;
        }

        if (this.validData) {
            try {
                this.isLoading = true;
                let savedDriver: Driver;
                if (!this.driver?.id) {
                    savedDriver = await this.createDriverAction({
                        driver: driverToUse,
                        company: this.selectedCompany
                    });
                } else {
                    savedDriver = await this.updateDriverAction({ driver: driverToUse });
                }
                await this.uploadAvatar(savedDriver?.id ?? this.driver?.id ?? '');
                this.dismiss(true);
            } catch (err) {
                switch (err.status) {
                    case 409:
                        this.$notifyErrorSimplified('DRIVER.ERRORS.DUPLICATE_SERIAL_ID');
                        break;
                    default:
                        this.$notifyErrorSimplified('DRIVER.ERRORS.CREATE_GENERAL');
                        break;
                }

                console.log(err);
            } finally {
                this.isLoading = false;
            }
        }
    }

    /**
     * True only for an existing driver whose promoter is being moved from one to
     * a different one. A first-time assignment (none -> some) and an unchanged
     * promoter both stay on the normal save path.
     */
    private get isPromoterBeingChanged(): boolean {
        if (!this.driver?.id || !this.originalPromoterId) {
            return false;
        }
        const selected = this.selectedPromoterId;
        return !!selected && selected !== this.originalPromoterId;
    }

    /**
     * The select binds on id, but a driver loaded from the list can still carry
     * the populated promoter object, so both shapes have to be handled.
     */
    private get selectedPromoterId(): string | null {
        const promoter = this.driverCopy.promoter;
        if (!promoter) {
            return null;
        }
        return typeof promoter === 'object' ? (promoter as Promoter).id : promoter;
    }

    private async openReassignDialog() {
        this.reassignmentPreview = null;
        this.showReassignDialog = true;
        try {
            this.reassignmentPreview = await this.previewReassignmentAction({
                driver: this.driver!.id,
                toPromoter: this.selectedPromoterId!
            });
        } catch (e) {
            this.$notifyErrorSimplified('DRIVER.REASSIGN.ERROR_PREVIEW');
            this.onReassignCancelled();
        }
    }

    private async onReassignConfirmed() {
        if (!this.reassignmentPreview) {
            return;
        }
        try {
            this.isApplyingReassignment = true;
            await this.applyReassignmentAction({
                driver: this.driver!.id,
                toPromoter: this.selectedPromoterId!,
                planHash: this.reassignmentPreview.planHash
            });

            // The move is done and the driver now belongs to the new promoter, so
            // the ordinary save below sees an unchanged promoter and only writes
            // the other edited fields.
            this.originalPromoterId = this.selectedPromoterId;
            this.showReassignDialog = false;
            this.reassignmentPreview = null;
            this.$notifySuccessSimplified('DRIVER.REASSIGN.SUCCESS');
            await this.saveData();
        } catch (e) {
            // 409 means the impact changed between preview and confirm -- the
            // admin has to see the new numbers rather than have them applied.
            if ((e as any)?.status === 409) {
                this.$notifyErrorSimplified('DRIVER.REASSIGN.ERROR_STALE');
                await this.openReassignDialog();
            } else {
                this.$notifyErrorSimplified('DRIVER.REASSIGN.ERROR_APPLY');
            }
        } finally {
            this.isApplyingReassignment = false;
        }
    }

    /**
     * Cancelling puts the dropdown back, so an abandoned dialog cannot leave the
     * form showing a promoter the driver does not have.
     */
    private onReassignCancelled() {
        this.showReassignDialog = false;
        this.reassignmentPreview = null;
        this.driverCopy.promoter = this.originalPromoterId ?? undefined;
    }

    public dismiss(reloadData: boolean = false) {
        if (reloadData) {
            this.driverCopy = new Driver();
        }

        this.$v.$reset();
        this.$emit('closeDialog', reloadData);
    }

    public checkForm(type: string) {
        this.validData = !this.$v!.$invalid;
        this.triggerValidation(type);
    }
}
